'use client';

import { useCallback, useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * `PwaInstallPrompt` — surfaces the browser's native install flow
 * via a lightweight, dismissable pill above the mobile bottom nav.
 *
 * Browser support matrix (2026):
 *   • Chromium (Android, desktop): fires `beforeinstallprompt` with
 *     a prompt() handle. We intercept it, suppress the default
 *     mini-infobar, and expose "Install" on our own chrome.
 *   • iOS Safari: does NOT fire `beforeinstallprompt`. We show a
 *     one-line help line pointing at the Share sheet instead.
 *     Detection uses `standalone` + UA heuristics.
 *   • Already installed: `display-mode: standalone` returns true,
 *     we render nothing.
 *
 * Dismissal is persisted for 30 days in localStorage so we don't
 * nag returning visitors. "Install" succeeding (and thus the
 * `appinstalled` event firing) clears any stored dismissal because
 * the prompt will never reappear anyway.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DISMISS_KEY = 'takeover:pwa-install-dismissed-at';
const DISMISS_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

function isDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < DISMISS_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function setDismissed() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // localStorage can throw in private browsing; swallow so the
    // pill just re-renders next session rather than crashing.
  }
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  // iOS: non-standard `navigator.standalone`; Android: display-mode.
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    nav.standalone === true ||
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    window.matchMedia?.('(display-mode: fullscreen)').matches === true
  );
}

function isIOSSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isSafari = /safari/.test(ua) && !/crios|fxios|edgios/.test(ua);
  return isIOS && isSafari;
}

export function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosHintOpen, setIosHintOpen] = useState(false);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    if (isStandalone() || isDismissed()) {
      setHidden(true);
      return;
    }

    const handler = (event: Event) => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
      setHidden(false);
    };
    const installed = () => {
      setDeferred(null);
      setIosHintOpen(false);
      setHidden(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installed);

    // iOS fallback — browser doesn't fire beforeinstallprompt, so
    // lean on UA detection and surface a hint pill instead.
    if (isIOSSafari()) setHidden(false);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const onInstall = useCallback(async () => {
    if (deferred) {
      try {
        await deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === 'accepted') {
          setHidden(true);
        } else {
          setDismissed();
          setHidden(true);
        }
      } catch {
        setHidden(true);
      }
      setDeferred(null);
    } else if (isIOSSafari()) {
      setIosHintOpen((v) => !v);
    }
  }, [deferred]);

  const onDismiss = useCallback(() => {
    setDismissed();
    setHidden(true);
    setIosHintOpen(false);
    setDeferred(null);
  }, []);

  if (hidden) return null;

  return (
    <div className="fixed bottom-[132px] left-1/2 -translate-x-1/2 z-[60] md:hidden pointer-events-none">
      <div className="pointer-events-auto flex items-stretch gap-0 rounded-[4px] border border-amber-400/50 bg-black/90 backdrop-blur-md shadow-[0_10px_24px_-12px_rgba(255,215,120,0.45)]">
        <button
          type="button"
          onClick={onInstall}
          className={cn(
            'flex items-center gap-2 px-3 h-9 text-[10px] font-mono uppercase tracking-[0.22em]',
            'text-amber-100 hover:text-amber-50 transition-colors',
          )}
        >
          <Download className="h-3.5 w-3.5" />
          Install app
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss install prompt"
          className="flex items-center justify-center w-9 h-9 border-l border-amber-400/30 text-zinc-400 hover:text-amber-100 hover:bg-white/5 transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {iosHintOpen && (
        <div className="pointer-events-auto mt-2 px-3 py-2 rounded-[4px] border border-amber-400/30 bg-black/90 backdrop-blur-md text-[10px] text-zinc-200 leading-snug max-w-[280px]">
          Tap the <span className="text-amber-100 font-semibold">Share</span> icon in Safari, then
          choose <span className="text-amber-100 font-semibold">Add to Home Screen</span> to
          install Takeover as an app.
        </div>
      )}
    </div>
  );
}
